// Llamadas a la bóveda (consent-vault) para el landing de acceso por
// subdominio (Rediseño mayor 2026-08-31, Módulos C/D). Las tres únicas rutas
// públicas de la bóveda que este landing necesita — ver
// packages/web/src/api/index.ts y lib/hce-auth.ts/hce-registro.ts.
import { getConfig } from './config';
import { cabecerasBoveda } from './audit/auditedFetch';
import type { Family } from './family-type';
import type { ResumenBajaProfesional, ResumenPreviaBaja, TraspasoDeBaja } from './bajaProfesional';

export function vaultUrl(path: string): string {
  const base = (getConfig().vaultUrl || '').replace(/\/+$/, '');
  return `${base}${path}`;
}

export type TenantPorSubdominio = {
  slug: string;
  clinicName: string;
  subdominio: string;
  hceAprovisionado: boolean;
  /** Encabezado de los PDF del HCE (2026-09-14) — `null` = no cargado en "Datos del consultorio"/"Correo del consultorio". */
  address: string | null;
  phone: string | null;
  correoRespuesta: string | null;
  /** Fork por familia (Fase 2, ítem 8) — ver lib/family-overrides.ts. */
  family: Family;
};

/** `null` si no hay ninguna clínica en este subdominio (o la bóveda no está configurada). */
export async function resolverTenantPorSubdominio(host: string): Promise<TenantPorSubdominio | null> {
  if (!getConfig().vaultUrl) return null;
  try {
    const res = await fetch(vaultUrl(`/api/tenant-por-subdominio/${encodeURIComponent(host)}`));
    if (!res.ok) return null;
    return (await res.json()) as TenantPorSubdominio;
  } catch {
    return null;
  }
}

export type LoginAdminResultado =
  | { ok: true; token: string; name: string }
  | {
      ok: false;
      error: string;
      /** La cuenta tiene la verificación en dos pasos activa: falta (o falló) el código. */
      mfaRequired?: true;
      /** No hay sesión: la cuenta tiene que activar la verificación en dos pasos (`forzado` = la política de la clínica ya lo exige). */
      mfaEnroll?: { token: string; forzado: boolean };
    };

export async function loginHceAdmin(
  subdominio: string,
  username: string,
  password: string,
  opciones: { mfaCode?: string; enrolarMfa?: boolean } = {},
): Promise<LoginAdminResultado> {
  const res = await fetch(vaultUrl('/api/hce/admin-login'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ subdominio, username, password, ...(opciones.mfaCode ? { mfaCode: opciones.mfaCode } : {}), ...(opciones.enrolarMfa ? { enrolarMfa: true } : {}) }),
  });
  const body = await res.json().catch(() => ({}));
  // MT-043-A/C: con la contraseña correcta y la política (o la persona) pidiendo el factor, el servidor entrega un token de enrolamiento SIN sesión
  // (403 si lo exige la política, 200 si lo pidió la persona). Con el factor activo, 401 `mfaRequired` hasta que llegue un código correcto.
  if (typeof body?.mfaEnrollToken === 'string' && body?.mfaEnrollRequired === true) {
    return { ok: false, error: body?.error || '', mfaEnroll: { token: body.mfaEnrollToken, forzado: res.status === 403 } };
  }
  if (!res.ok) return { ok: false, error: body?.error || 'No se pudo iniciar sesión.', ...(body?.mfaRequired === true ? { mfaRequired: true as const } : {}) };
  return { ok: true, token: body.token, name: body.name };
}

/**
 * Autoservicio de "Recuperar contraseña" para el rol admin (2026-09-13,
 * pedido de Sue). Nunca lanza y nunca revela si la cuenta existe — mismo
 * criterio que el reset de Medplum de FormularioRecuperar: el caller siempre
 * muestra el mismo mensaje de éxito, haya o no una cuenta con ese usuario.
 */
export async function recuperarAdminHce(subdominio: string, username: string): Promise<void> {
  await fetch(vaultUrl('/api/hce/admin-recuperar'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ subdominio, username }),
  }).catch(() => {});
}

// --- Activación de cuenta por enlace (2026-09-13) — reemplaza al autorregistro
// público: las cuentas las crea el admin y la persona elige su contraseña con
// el enlace que le llega por correo. Sin sesión: la credencial es el token. ---

export type ActivacionHce = { username: string; clinicName: string };

export async function consultarActivacionHce(token: string): Promise<{ ok: true; datos: ActivacionHce } | { ok: false; error: string }> {
  try {
    const res = await fetch(vaultUrl(`/api/hce/activacion/${encodeURIComponent(token)}`));
    const body = await res.json().catch(() => ({}));
    if (!res.ok) return { ok: false, error: body?.error || 'El enlace no es válido.' };
    return { ok: true, datos: { username: String(body.username ?? ''), clinicName: String(body.clinicName ?? '') } };
  } catch {
    return { ok: false, error: 'No se pudo verificar el enlace. Revisá tu conexión e intentá de nuevo.' };
  }
}

export async function activarCuentaHce(token: string, password: string): Promise<CambiarEstadoResultado> {
  const res = await fetch(vaultUrl(`/api/hce/activacion/${encodeURIComponent(token)}`), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ password }),
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: body?.error || 'No se pudo guardar la contraseña.' };
}

// --- Módulo E — panel de administración de usuarios (rol admin) ---

export type HceUsuarioListado = {
  id: number;
  username: string;
  name: string;
  role: 'admin' | 'personal-medico' | 'director-tecnico-farmacia';
  active: boolean;
  especialidadCodigo: string | null;
  /** Solo personal-medico. Matrícula/licencia profesional — Modo Auditor la verifica. */
  matricula: string | null;
  /** Solo personal-medico (Sala del HCE): consultorio físico habitual ("Consultorio 3"), que fija el admin y se copia al turno al llamar. */
  consultorio: string | null;
  createdAt: string;
  /** MT-043-C: estado de la verificación en dos pasos de la cuenta (solo si está activa, desde cuándo y si está bloqueada; nunca secretos). */
  mfa?: { activo: boolean; desde: string | null; bloqueado: boolean };
  /** MT-043-C: esta fila es la cuenta de quien tiene la sesión (no se reinicia ni se desbloquea a sí misma). */
  propia?: boolean;
};

export type ListaHceUsuarios = { usuarios: HceUsuarioListado[]; cupoPersonalMedico: number; personalMedicoActivos: number };

/** `null` = token inválido/expirado (401) — el caller debe mandar de vuelta al login. */
export async function listarHceUsuarios(token: string): Promise<ListaHceUsuarios | null> {
  const res = await fetch(vaultUrl('/api/hce/usuarios'), { headers: { authorization: `Bearer ${token}` } });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error('No se pudo cargar la lista de usuarios.');
  return (await res.json()) as ListaHceUsuarios;
}

export type CambiarEstadoResultado = { ok: true } | { ok: false; error: string };

/** Como `CambiarEstadoResultado`, más lo que el servidor informa al dar de baja a un médico (qué pasó con su Sala). Ver lib/bajaProfesional.ts. */
export type BajaUsuarioResultado =
  | { ok: true; baja?: ResumenBajaProfesional | null; reasignacionFallida?: boolean; traspaso?: TraspasoDeBaja | null }
  | { ok: false; error: string };

/** Lo que se suma a la baja (C11.3): con citas por atender el servidor exige `reemplazoId` o `dejarSinMedico: true`. Ver `cargaDeBaja`. */
export type DecisionDeBaja = { reemplazoId?: string; dejarSinMedico?: true };

export async function cambiarEstadoHceUsuario(token: string, id: number, active: boolean, decision: DecisionDeBaja = {}): Promise<BajaUsuarioResultado> {
  const res = await fetch(vaultUrl(`/api/hce/usuarios/${id}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ active, ...(active ? {} : decision) }),
  });
  if (res.ok) {
    const body = await res.json().catch(() => ({}));
    return { ok: true, baja: body?.baja ?? null, reasignacionFallida: body?.reasignacionFallida === true, traspaso: body?.traspaso ?? null };
  }
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: body?.error || 'No se pudo actualizar el usuario.' };
}

export type ResumenBajaResultado = { ok: true; resumen: ResumenPreviaBaja } | { ok: false; error: string };

/** Antes de la baja (C11.3): qué deja sin médico y a quién se puede pasar. Lectura pura: la baja (PATCH) vuelve a validar todo. */
export async function resumenBajaHceUsuario(token: string, id: number): Promise<ResumenBajaResultado> {
  const res = await fetch(vaultUrl(`/api/hce/usuarios/${id}/baja-resumen`), { headers: { authorization: `Bearer ${token}` } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: body?.error || 'No se pudo revisar las citas de este médico.' };
  return { ok: true, resumen: body as ResumenPreviaBaja };
}

/** Modo Auditor (2026-09-09) — edición de matrícula desde el panel de admin, mismo endpoint PATCH que cambiarEstadoHceUsuario, otro campo del body. */
export async function actualizarMatriculaHceUsuario(token: string, id: number, matricula: string): Promise<CambiarEstadoResultado> {
  const res = await fetch(vaultUrl(`/api/hce/usuarios/${id}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ matricula }),
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: body?.error || 'No se pudo actualizar la matrícula.' };
}

/** BA-C-11 (D-038) — el admin corrige la especialidad de una cuenta (de ella sale la profesión que el servidor comprueba); mismo endpoint PATCH, otro campo del body. */
export async function actualizarEspecialidadHceUsuario(token: string, id: number, especialidadCodigo: string): Promise<CambiarEstadoResultado> {
  const res = await fetch(vaultUrl(`/api/hce/usuarios/${id}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ especialidadCodigo }),
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: body?.error || 'No se pudo cambiar la especialidad.' };
}

/** Sala del HCE (rebanada 5) — el admin fija el consultorio habitual de un médico; mismo endpoint PATCH, otro campo del body. */
export async function actualizarConsultorioHceUsuario(token: string, id: number, consultorio: string): Promise<CambiarEstadoResultado> {
  const res = await fetch(vaultUrl(`/api/hce/usuarios/${id}`), {
    method: 'PATCH',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify({ consultorio }),
  });
  if (res.ok) return { ok: true };
  const body = await res.json().catch(() => ({}));
  return { ok: false, error: body?.error || 'No se pudo guardar el consultorio.' };
}

// Admin mínimo del consultorio (2026-09-13, retiro del legado packages/hce).

export type InvitacionHceInput = {
  tipo: 'admin' | 'personal-medico' | 'director-tecnico-farmacia';
  /** Correo de la persona: es su usuario y a donde le llega el enlace de activación. */
  username: string;
  nombre: string;
  apellido?: string;
  /** Solo aplica a tipo === "personal-medico" — no es una especialidad médica para farmacia interna. */
  especialidadCodigo?: string;
  /** Obligatorio si tipo === "personal-medico" o "director-tecnico-farmacia" — Modo Auditor la verifica. */
  matricula?: string;
  /** Obligatoria si tipo === "personal-medico" o "director-tecnico-farmacia" — la que después se compara contra el certificado .p12 en "Mi certificado". */
  cedula?: string;
};

export type ResultadoConCorreo = { ok: true; correoEnviado: boolean; detalleCorreo: string } | { ok: false; error: string };

async function postAdminConCorreo(token: string, path: string, body: unknown, errorPorDefecto: string): Promise<ResultadoConCorreo> {
  const res = await fetch(vaultUrl(path), {
    method: 'POST',
    headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
    body: JSON.stringify(body),
  });
  const cuerpo = await res.json().catch(() => ({}));
  if (!res.ok) return { ok: false, error: cuerpo?.error || errorPorDefecto };
  return { ok: true, correoEnviado: cuerpo?.correoEnviado === true, detalleCorreo: String(cuerpo?.detalleCorreo ?? '') };
}

/** Crea la cuenta desde el panel (cupo, matrícula) y le manda a la persona el enlace para crear su contraseña. */
export async function invitarHceUsuario(token: string, input: InvitacionHceInput): Promise<ResultadoConCorreo> {
  return postAdminConCorreo(token, '/api/hce/usuarios', input, 'No se pudo crear la cuenta.');
}

/** "Cambiar contraseña": le manda a la persona el enlace para crear una nueva (el admin no la elige ni la ve). */
export async function enviarEnlaceContrasenaHceUsuario(token: string, id: number): Promise<ResultadoConCorreo> {
  return postAdminConCorreo(token, `/api/hce/usuarios/${id}/enlace-contrasena`, {}, 'No se pudo enviar el enlace.');
}

/** Reactiva una cuenta dada de baja (Personal Médico / Director Técnico: con el MISMO Practitioner) y le manda el enlace para crear su contraseña. */
export async function reactivarHceUsuario(token: string, id: number): Promise<ResultadoConCorreo> {
  return postAdminConCorreo(token, `/api/hce/usuarios/${id}/reactivar`, {}, 'No se pudo reactivar la cuenta.');
}

// --- Módulo G — Dashboard (citas del practicante logueado + KPIs) ---
// Autenticadas con el Bearer del propio Medplum del practicante
// (cabecerasBoveda) — a diferencia de admin-login/usuarios, que usan el
// token propio de hce-auth.ts. Nunca se llega acá sin sesión de Medplum.

import type { EtiquetaCita } from '@/dashboard/etiquetaCita';

export type RangoDashboard = 'dia' | 'semana' | 'mes';

export type CitaDashboard = {
  citaId: string;
  startsAt: string | null;
  endsAt: string | null;
  contactName: string;
  motivo: string | null;
  modalidad: 'presencial' | 'telemedicina';
  meetingUrl: string | null;
  /** Etiqueta unificada de estado (Programada, En sala, Llamado, En consulta, Atendida, Se fue…): la calcula el servidor con la Sala, igual que el calendario del CRM. Opcional por si responde un servidor anterior a la Sala: la pantalla cae a un respaldo neutro. */
  etiqueta?: EtiquetaCita;
  /** Código del turno de Sala (`A-01`…); `null` si la cita no tiene turno. */
  turno?: string | null;
  medplumPatientId: string | null;
};

export type CitasDashboard = { citas: CitaDashboard[]; sinEspecialidadVinculada: boolean };

export async function obtenerCitasDashboard(rango: RangoDashboard, fecha: Date): Promise<CitasDashboard> {
  const url = new URL(vaultUrl('/api/hce/citas'));
  url.searchParams.set('rango', rango);
  url.searchParams.set('fecha', fecha.toISOString());
  const res = await fetch(url.toString(), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudieron cargar las citas.');
  return (await res.json()) as CitasDashboard;
}

export type KpisDashboard = { totalCitas: number; pacientesNuevos: number; pacientesRecurrentes: number; sinEspecialidadVinculada: boolean };

export async function obtenerKpisDashboard(rango: RangoDashboard, fecha: Date): Promise<KpisDashboard> {
  const url = new URL(vaultUrl('/api/hce/citas-kpi'));
  url.searchParams.set('rango', rango);
  url.searchParams.set('fecha', fecha.toISOString());
  const res = await fetch(url.toString(), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudieron cargar los indicadores.');
  return (await res.json()) as KpisDashboard;
}

// --- Widgets de "hoy" (Rediseño mayor 2026-09-01) — siempre la fecha actual
// del server, independiente de qué rango esté mirando el calendario navegable. ---

export type AlertaClinica = {
  patientId: string;
  patientName: string;
  tipo: 'alergia' | 'laboratorio';
  detalle: string;
};

export type ResumenHoyDashboard = {
  pacientesHoy: number;
  citas: CitaDashboard[];
  alertas: AlertaClinica[];
  sinEspecialidadVinculada: boolean;
};

export async function obtenerResumenHoy(): Promise<ResumenHoyDashboard> {
  const res = await fetch(vaultUrl('/api/hce/dashboard-hoy'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudo cargar el resumen de hoy.');
  return (await res.json()) as ResumenHoyDashboard;
}

// --- Tareas pendientes (Rediseño mayor 2026-09-01) ---

export type TareaDashboard = { id: number; texto: string; fechaHora: string | null; completada: boolean };

export async function listarTareasDashboard(): Promise<TareaDashboard[]> {
  const res = await fetch(vaultUrl('/api/hce/tareas'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudieron cargar las tareas.');
  const body = (await res.json()) as { tareas: TareaDashboard[] };
  return body.tareas;
}

export async function crearTareaDashboard(texto: string, fechaHora: string | null): Promise<TareaDashboard> {
  const res = await fetch(vaultUrl('/api/hce/tareas'), {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ texto, fechaHora }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'No se pudo crear la tarea.');
  return body as TareaDashboard;
}

export async function actualizarTareaDashboard(
  id: number,
  cambios: { texto?: string; fechaHora?: string | null; completada?: boolean },
): Promise<TareaDashboard> {
  const res = await fetch(vaultUrl(`/api/hce/tareas/${id}`), {
    method: 'PATCH',
    headers: cabecerasBoveda(),
    body: JSON.stringify(cambios),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body?.error || 'No se pudo actualizar la tarea.');
  return body as TareaDashboard;
}

export async function borrarTareaDashboard(id: number): Promise<void> {
  const res = await fetch(vaultUrl(`/api/hce/tareas/${id}`), { method: 'DELETE', headers: cabecerasBoveda() });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body?.error || 'No se pudo borrar la tarea.');
  }
}

// --- Pacientes (Rediseño mayor 2026-09-02, Fase 1 "destinos reales del nav") ---

export type PacienteHce = {
  patientId: number;
  nombre: string;
  idNumber: string;
  phone: string;
  medplumPatientId: string | null;
};

export async function listarPacientesHce(): Promise<PacienteHce[]> {
  const res = await fetch(vaultUrl('/api/hce/pacientes'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudo cargar la lista de pacientes.');
  const body = (await res.json()) as { pacientes: PacienteHce[] };
  return body.pacientes;
}

// --- Alta de paciente nuevo (Rediseño mayor 2026-09-02, Fase 6) ---

export type ConsentBlock =
  | { kind: 'paragraph'; text: string }
  | { kind: 'note'; text: string }
  | { kind: 'legalBasis'; text: string }
  | { kind: 'subheading'; text: string }
  | { kind: 'list'; items: string[] }
  | { kind: 'table'; rows: { label: string; value: string }[] }
  | { kind: 'definitions'; items: { term: string; text: string }[] };

export type ConsentSection = { number: number; title: string; blocks: ConsentBlock[] };
export type OptionalAuthorization = { key: 'difusion' | 'whatsappMeta'; title: string; text: string; required?: boolean };

export type ConsentContent = {
  title: string;
  subtitle?: string;
  sections: ConsentSection[];
  acceptLabel: string;
  optionalAuthorizations: OptionalAuthorization[];
  footerNote?: string;
};

export async function obtenerTextoConsentimiento(): Promise<ConsentContent> {
  const res = await fetch(vaultUrl('/api/hce/consentimiento-texto'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudo cargar el texto legal del consentimiento.');
  return (await res.json()) as ConsentContent;
}

export type NuevoPacienteInput = {
  firstName: string;
  lastName: string;
  idType: 'cedula' | 'pasaporte';
  idNumber: string;
  phone: string;
  email?: string;
  birthDate: string;
  consent: {
    signatureDataUrl: string;
    optDifusion: boolean;
    optWhatsappMeta: boolean;
    aceptado: boolean;
    representative?: { fullName: string; idNumber: string; relation: string } | null;
  };
};

export type ResultadoCrearPaciente =
  | { ok: true; patientId: number; medplumPatientId: string }
  // `patientId` presente cuando el paciente SÍ quedó creado localmente pero
  // la sincronización a Medplum falló (se reintenta sola, ver
  // lib/pacientes-medplum-sweep.ts en el backend) — distinto de "no se creó".
  | { ok: false; detail: string; patientId?: number };

export async function crearPacienteConsulta(input: NuevoPacienteInput): Promise<ResultadoCrearPaciente> {
  const res = await fetch(vaultUrl('/api/hce/pacientes'), {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify(input),
  });
  return (await res.json()) as ResultadoCrearPaciente;
}

// --- Historias (Rediseño mayor 2026-09-02, Fase 3 "destinos reales del nav") ---

export type EntradaHistoria = { patientId: string; patientName: string; fecha: string; texto: string };
export type FeedHistorias = { entradas: EntradaHistoria[]; totalPacientes: number; pacientesIncluidos: number };

export async function obtenerFeedHistorias(): Promise<FeedHistorias> {
  const res = await fetch(vaultUrl('/api/hce/historias-feed'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudo cargar el feed de historias.');
  return (await res.json()) as FeedHistorias;
}

// --- Agenda (Rediseño mayor 2026-09-02, Fase 4 "destinos reales del nav") ---

export type EspecialidadHce = { id: string; name: string };

export async function obtenerMisEspecialidades(): Promise<EspecialidadHce[]> {
  const res = await fetch(vaultUrl('/api/hce/mis-especialidades'), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudieron cargar tus especialidades.');
  const body = (await res.json()) as { especialidades: EspecialidadHce[] };
  return body.especialidades;
}

export type ResultadoAgendaHce = { ok: boolean; detail: string; citaId?: string };

export async function crearCitaHce(input: {
  patientId: number;
  contactName: string;
  phone: string;
  startsAtIso: string;
  specialtyId: string;
  notes?: string;
  modalidad?: 'presencial' | 'telemedicina';
}): Promise<ResultadoAgendaHce> {
  const res = await fetch(vaultUrl('/api/hce/agenda/citas'), {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify(input),
  });
  return (await res.json()) as ResultadoAgendaHce;
}

export async function reagendarCitaHce(citaId: string, startsAtIso: string): Promise<ResultadoAgendaHce> {
  const res = await fetch(vaultUrl(`/api/hce/agenda/citas/${citaId}`), {
    method: 'PATCH',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ startsAtIso }),
  });
  return (await res.json()) as ResultadoAgendaHce;
}

export async function cancelarCitaHce(citaId: string, reason: string): Promise<ResultadoAgendaHce> {
  const res = await fetch(vaultUrl(`/api/hce/agenda/citas/${citaId}`), {
    method: 'DELETE',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ reason }),
  });
  return (await res.json()) as ResultadoAgendaHce;
}

// --- Seguimiento IA (Fase 6/7 de "receta digital") ---
//
// Mismo motor que ya usa el CRM (lib/seguimientos.ts) — estas llamadas
// programan/listan/reprograman/cancelan con `origen:'hce'` server-side, que
// cifra el texto de la nota (lib/notas-clinicas.ts, ver el aviso de
// privacidad en la UI). El texto viaja en CLARO en la respuesta de estos
// endpoints: el médico tratante logueado es quien lo escribió.

export type EstadoSeguimientoHce = 'scheduled' | 'sent' | 'responded' | 'cancelled' | 'failed';

export type SeguimientoHce = {
  id: number;
  patientId: number;
  patientNoteId: number;
  scheduledFor: string;
  status: EstadoSeguimientoHce;
  sentAt: string | null;
  sentMessageText: string | null;
  lastError: string | null;
  noteBody: string;
  replies: { id: number; text: string; repliedAt: string }[];
};

export async function crearSeguimientoHce(input: {
  patientId: number;
  noteBody: string;
  scheduledFor: string;
  practitionerName?: string;
}): Promise<SeguimientoHce> {
  const res = await fetch(vaultUrl('/api/hce/seguimientos'), {
    method: 'POST',
    headers: cabecerasBoveda(),
    body: JSON.stringify(input),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'No se pudo programar el seguimiento.');
  return body.followup as SeguimientoHce;
}

/** `patientId` opcional: sin él, trae TODOS los del tenant del Bearer (no se usa así hoy — la pestaña siempre lo pasa). */
export async function listarSeguimientosHce(patientId: number): Promise<SeguimientoHce[]> {
  const res = await fetch(vaultUrl(`/api/hce/seguimientos?patientId=${patientId}`), { headers: cabecerasBoveda() });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'No se pudieron cargar los seguimientos.');
  return (body.seguimientos ?? []) as SeguimientoHce[];
}

export async function reprogramarSeguimientoHce(id: number, scheduledForIso: string): Promise<SeguimientoHce> {
  const res = await fetch(vaultUrl(`/api/hce/seguimientos/${id}`), {
    method: 'PATCH',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ scheduledFor: scheduledForIso }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'No se pudo reprogramar el seguimiento.');
  return body.followup as SeguimientoHce;
}

export async function cancelarSeguimientoHce(id: number, practitionerName?: string): Promise<SeguimientoHce> {
  const res = await fetch(vaultUrl(`/api/hce/seguimientos/${id}`), {
    method: 'DELETE',
    headers: cabecerasBoveda(),
    body: JSON.stringify({ practitionerName }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || 'No se pudo cancelar el seguimiento.');
  return body.followup as SeguimientoHce;
}

// --- Reportes (Rediseño mayor 2026-09-02, Fase 5 "destinos reales del nav") ---

export type ReportesHce = {
  totalCitas: number;
  pacientesNuevos: number;
  pacientesRecurrentes: number;
  noShowCount: number;
  noShowRate: number | null;
  sinEspecialidadVinculada: boolean;
};

export async function obtenerReportes(desde: Date, hasta: Date): Promise<ReportesHce> {
  const url = new URL(vaultUrl('/api/hce/reportes'));
  url.searchParams.set('desde', desde.toISOString());
  url.searchParams.set('hasta', hasta.toISOString());
  const res = await fetch(url.toString(), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudieron cargar los reportes.');
  return (await res.json()) as ReportesHce;
}

/** Descarga el CSV — el endpoint exige el Bearer del practicante, así que no se puede linkear directo con <a href>; se trae como blob y se dispara la descarga desde el navegador. */
export async function descargarReportesCsv(desde: Date, hasta: Date): Promise<void> {
  const url = new URL(vaultUrl('/api/hce/reportes/export'));
  url.searchParams.set('desde', desde.toISOString());
  url.searchParams.set('hasta', hasta.toISOString());
  const res = await fetch(url.toString(), { headers: cabecerasBoveda() });
  if (!res.ok) throw new Error('No se pudo generar el CSV.');
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = `reportes-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
}

// --- Dashboard — exportación completa del consultorio (cumplimiento legal) y backup ---
// Mismo patrón que descargarReportesCsv (blob + <a> disparado a mano), pero con el token del ADMINISTRADOR del HCE (BA-C-10).

/** Resultado de una descarga de la exportación completa: `incompleta` = el servidor avisó (cabeceras X-Exportacion-*) que omitió la ficha de algún paciente. */
export type ResultadoDescargaExportacion = { incompleta: boolean; omitidos: number; /** H-019: SHA-256 (hex) del archivo, de la cabecera `X-Exportacion-SHA256`; ausente si el servidor no la mandó. */ sha256?: string };

async function descargarArchivoDeBoveda(path: string, errorMensaje: string, tokenAdmin: string): Promise<ResultadoDescargaExportacion> {
  // BA-C-10: estas descargas son solo del administrador del HCE (su token); un 403/503 trae el motivo en `error` y se le muestra tal cual.
  const res = await fetch(vaultUrl(path), { headers: { authorization: `Bearer ${tokenAdmin}` } });
  if (!res.ok) {
    const motivo = await res.json().then((j: unknown) => (typeof (j as { error?: unknown })?.error === 'string' ? (j as { error: string }).error : null)).catch(() => null);
    throw new Error(motivo ?? errorMensaje);
  }
  const disposition = res.headers.get('content-disposition') ?? '';
  const nombreSugerido = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'descarga';
  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = nombreSugerido;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(objectUrl);
  const incompleta = res.headers.get('x-exportacion-incompleta') === 'true';
  const sha256 = res.headers.get('x-exportacion-sha256');
  return { incompleta, omitidos: incompleta ? Number(res.headers.get('x-exportacion-omitidos')) || 0 : 0, ...(sha256 && /^[0-9a-f]{64}$/.test(sha256) ? { sha256 } : {}) };
}

/** "Exportar datos" — cumplimiento legal (sección 8.7 generalizada a todo el tenant), Bundle FHIR completo. `tokenAdmin` obligatorio (BA-C-10): es el token de hce_users del administrador del HCE; el servidor rechaza (403) la sesión de un profesional. */
export async function descargarExportacionLegal(tokenAdmin: string): Promise<ResultadoDescargaExportacion> {
  return descargarArchivoDeBoveda('/api/hce/exportacion-legal/datos', 'No se pudo generar la exportación de datos.', tokenAdmin);
}

/** "Backup" — mismo Bundle FHIR, mismo dato. */
export async function descargarBackupHce(tokenAdmin: string): Promise<ResultadoDescargaExportacion> {
  return descargarArchivoDeBoveda('/api/hce/backup/datos', 'No se pudo generar el backup.', tokenAdmin);
}

// --- Salida del consultorio: «Descargar mis historias» (período de descarga tras la baja) ---

export type EstadoSalida = {
  enDescarga: boolean;
  estado?: 'en-descarga' | 'descarga-vencida';
  consultorio?: string;
  inicioEl?: string | null;
  venceEl?: string | null;
  diasTotales?: number | null;
  diasRestantes?: number | null;
  descargas?: number;
  ultimaDescargaEl?: string | null;
  ultimaDescargaSha256?: string | null;
  ultimaDescargaIncompleta?: boolean;
};

/** Error con un mensaje ya en español para la persona (no el texto crudo de la red ni de un navegador en inglés). */
export class ErrorSalida extends Error {
  constructor(
    message: string,
    readonly tipo: 'sesion-o-plazo' | 'incompleta' | 'otro',
    readonly omitidos = 0,
  ) {
    super(message);
    this.name = 'ErrorSalida';
  }
}

const MSG_SIN_CONEXION = 'No pudimos comunicarnos con el servidor. Revise su conexión a internet e inténtelo de nuevo.';

/** Estado del período de descarga del consultorio del token. 401 = la sesión terminó o el plazo de descarga venció. */
export async function obtenerEstadoSalida(tokenAdmin: string): Promise<EstadoSalida> {
  let res: Response;
  try {
    res = await fetch(vaultUrl('/api/hce/salida/estado'), { headers: { authorization: `Bearer ${tokenAdmin}` } });
  } catch {
    throw new ErrorSalida(MSG_SIN_CONEXION, 'otro');
  }
  if (res.status === 401) throw new ErrorSalida('Su sesión terminó o el plazo de descarga de su consultorio venció. Si necesita más tiempo, escriba a Logiacaremedical S.A.S.', 'sesion-o-plazo');
  if (!res.ok) throw new ErrorSalida('No se pudo consultar el período de descarga. Inténtelo de nuevo en unos minutos.', 'otro');
  return (await res.json()) as EstadoSalida;
}

export type ResultadoDescargaSalida = {
  nombreArchivo: string;
  bytes: number;
  /** Huella SHA-256 que informó el servidor ('' si no la informó). */
  sha256Servidor: string;
  /** Huella SHA-256 calculada aquí, sobre los bytes que de verdad llegaron. */
  sha256Navegador: string;
  /** 'si' = coinciden; 'no' = el servidor informó una huella distinta (descarga dañada); 'no-verificable' = el servidor no informó huella (no se puede comprobar, no es lo mismo que dañada). */
  verificacion: 'si' | 'no-verificable';
  completo: boolean;
  omitidos: number;
};

export async function sha256Hex(datos: ArrayBuffer): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', datos);
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
}

/** Armar el paquete de un consultorio grande puede tardar varios minutos. */
export const TIEMPO_MAXIMO_DESCARGA_MS = 20 * 60_000;

/**
 * Descarga el paquete completo (ZIP). Autenticada con el token del administrador (no hay enlace que se pueda reenviar): se trae como blob, se verifica su huella
 * contra la que informó el servidor y se dispara el guardado desde el navegador. Una copia INCOMPLETA no se entrega salvo `aceptarIncompleta`.
 */
export async function descargarPaqueteSalida(tokenAdmin: string, opciones: { aceptarIncompleta?: boolean } = {}): Promise<ResultadoDescargaSalida> {
  const control = new AbortController();
  const temporizador = setTimeout(() => control.abort(), TIEMPO_MAXIMO_DESCARGA_MS);
  try {
    let res: Response;
    try {
      res = await fetch(vaultUrl(`/api/hce/salida/descarga${opciones.aceptarIncompleta ? '?aceptarIncompleta=1' : ''}`), { headers: { authorization: `Bearer ${tokenAdmin}` }, signal: control.signal });
    } catch (err) {
      if ((err as { name?: string })?.name === 'AbortError') throw new ErrorSalida('La preparación tardó demasiado y se canceló. Inténtelo de nuevo; si persiste, escriba a Logiacaremedical S.A.S.', 'otro');
      throw new ErrorSalida(MSG_SIN_CONEXION, 'otro');
    }
    if (!res.ok) {
      const j = await res.json().catch(() => ({}) as Record<string, unknown>);
      const motivo = typeof j?.error === 'string' ? j.error : null;
      if (res.status === 401) throw new ErrorSalida('Su sesión terminó o el plazo de descarga de su consultorio venció. Si necesita más tiempo, escriba a Logiacaremedical S.A.S.', 'sesion-o-plazo');
      if (j?.codigo === 'incompleta') throw new ErrorSalida(motivo ?? 'La copia quedó incompleta.', 'incompleta', Number(j.omitidos) || 0);
      throw new ErrorSalida(motivo ?? 'No se pudo preparar la descarga. Inténtelo de nuevo; si persiste, escriba a Logiacaremedical S.A.S.', 'otro');
    }
    const nombreArchivo = /filename="([^"]+)"/.exec(res.headers.get('content-disposition') ?? '')?.[1] ?? 'historias.zip';
    let buffer: ArrayBuffer;
    try {
      buffer = await res.arrayBuffer();
    } catch {
      throw new ErrorSalida('La descarga se interrumpió antes de terminar. Inténtelo de nuevo.', 'otro');
    }
    const sha256Servidor = res.headers.get('x-exportacion-paquete-sha256') ?? '';
    const sha256Navegador = await sha256Hex(buffer);
    // La huella se compara ANTES de guardar: un archivo dañado no se deja en la carpeta de descargas como si estuviera bien.
    if (sha256Servidor !== '' && sha256Servidor !== sha256Navegador) {
      throw new ErrorSalida('El archivo llegó dañado (no es idéntico al que preparó el servidor) y NO se guardó. Vuelva a descargar.', 'otro');
    }
    const objectUrl = URL.createObjectURL(new Blob([buffer], { type: 'application/zip' }));
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = nombreArchivo;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(objectUrl);
    return {
      nombreArchivo,
      bytes: buffer.byteLength,
      sha256Servidor,
      sha256Navegador,
      verificacion: sha256Servidor === '' ? 'no-verificable' : 'si',
      completo: res.headers.get('x-exportacion-paquete-completo') === 'true',
      omitidos: Number(res.headers.get('x-exportacion-paquete-omitidos')) || 0,
    };
  } finally {
    clearTimeout(temporizador);
  }
}
